import { type TextAlign } from '@myre/common-sheet';
import { type Tooltip } from '@myre/ui-primitive-tooltip-directive';

import { type TemplateRef } from '@angular/core';

import { type Cell } from './cells.interfaces';

type Header<Key> = {
  /**
   * The header's key is used to link it with the table data (and subheaders).
   * The key must be unique and identical to the data's one (and subheader).
   */
  key: Key;
  /**
   * Can be a string or a key for translation.
   */
  label?: string;
};

export type Column<Key extends string> = Header<Key> & {
  /**
   * The width of the column.
   * If the value is a weight (default), the string corresponds to the flex attribute of the column.
   * If the width is specified in pixels, the string must be suffixed with the unit.
   */
  width?: string;
  maxWidth?: string;
  align?: TextAlign;
  canSort?: boolean;
  onClick?: (event: MouseEvent) => void;
  /**
   * Rendered after the label, e.g. a filter trigger. Its clicks and key strokes never reach the sort header.
   */
  headerTemplate?: TemplateRef<{ $implicit: Column<Key> }>;
};

export type Row<Key extends string> = {
  /**
   * The identifier of the row.
   */
  id: string | number;

  /**
   * The key of each row must correspond to the key of the corresponding header.
   */
  cells: Record<Key, Cell>;

  /**
   * The callback function to call when a row is clicked.
   */
  onClick?: (event: Event) => void;

  onCheckboxClick?: (event: MouseEvent) => boolean | void;

  checkboxState?: RowCheckboxState;

  /**
   * If true, the row will display loading placeholders instead of actual content.
   */
  isLoading?: boolean;

  /**
   * If true, the row is greyed out and excluded: its checkbox is unchecked and readonly.
   */
  isDisabled?: boolean;
};

export type Table<Key extends string> = {
  /**
   * Allows setting specific subheaders for the table.
   * For example, if you want to display totals by category.
   */
  subHeaders?: Header<Key>[];

  /**
   * The rows to display in the table with their respective cells and parameters.
   */
  rows?: Row<Key>[];

  /**
   * Specifies whether the table is open or closed by default.
   * (This parameter is functional only if there are multiple tables)
   */
  isOpen?: boolean;

  /**
   * Lock the collapse state in the default configured state (isOpen attribute). Default to false.
   */
  isCollapseFixed?: boolean;
};

export interface RowCheckboxState {
  /**
   * Whether the row is selected or not.
   */
  checked: boolean;

  /**
   * Whether the row is readonly or not.
   */
  readonly?: boolean;

  disabled?: boolean;

  /**
   * Shown when hovering the checkbox cell, e.g. to explain why the row is readonly.
   */
  tooltip?: Tooltip;

  /**
   * If a map is returned, rows will be updated with the new checkbox states.
   */
  afterChanged?: (
    checked: boolean,
    rowIdCheckboxStateMap: Map<string | number, RowCheckboxState>,
  ) => void | Map<string | number, RowCheckboxState>;
}

export enum MultiTableType {
  Actions = 'actions',
}

type MultiTableFooter = {
  type: MultiTableType;
};

type FooterButton = {
  label: string;
  onClick: () => void;
  loading?: boolean;
  icon?: string;
};

export interface MultiTableActionsFooter extends MultiTableFooter {
  type: MultiTableType.Actions;
  leftButtons: FooterButton[] | FooterButton;
  rightButton: FooterButton;
}

export interface TrustedMultiTableActionsFooter extends MultiTableActionsFooter {
  leftButtons: FooterButton[];
}
